import { useEffect, useState } from "react";
import {
  getAllMatches,
  fetchUserDetails,
  getTournamentData as getStaticTournamentData,
  getActiveTournament,
  getAllPlayers,
  getAllEvents,
} from "./api/api";
import "./App.scss";
import {
  setIsApplicationBootstrapped,
  setAdmin,
} from "./features/app/appSlice";
import {
  addEvent,
  updateEvent,
  deleteEvent,
  setGroupMatches,
  updateGroupMatch,
  updateLocationOnAllMatches,
} from "./features/matches/matchesSlice";
import { useAppDispatch, useAppSelector } from "./hooks";
import { Route, Routes } from "react-router-dom";
import Table from "./screens/table/table";
import Matches from "./screens/matches/matches";
import { setFavoriteTeams, setTeams } from "./features/teams/TeamsSlice";
import {
  GroupType,
  Match,
  LocationType,
  TeamType,
  ROLES,
  Pulje,
  Tournament,
  Referee,
  SpecielMatchReferee,
} from "./types/types";
import { setGroups } from "./features/groups/GroupsSlice";
import { setPuljer, setSelectedPulje } from "./features/puljer/PuljeSlice";
import LoadingIcon from "./components/loading-icon";
import ErrorPage from "./screens/other/errorPage";
import {
  setLocations,
  updateLocation,
} from "./features/locations/locationsSlice";
import KnockOut from "./screens/knockout/knockout";
import FrontPage from "./screens/other/frontPage";
import { removeToast } from "./features/notification/notificationSlice";
import Toast from "./components/notification/Toast";
import { SxProps } from "@mui/material";
import NotificationService from "./components/notification/NotificationService";
import Admin from "./screens/admin/admin";
import Slideshow from "./screens/slideshow/slideshow";
import {
  generateGroupsWithTeams,
  generateTeamsWithGroup,
  getFavoriteTeamsFromCookies,
} from "./utils";
import { setTournament } from "./features/tournament/tournamentSlice";
import { signOut, supabase } from "./supabase";
import { setClubs } from "./features/clubs/clubsSlice";
import { setReferees } from "./features/referees/refereesSlice";
import { RealtimeChannel } from "@supabase/supabase-js";
import MatchDetails from "./screens/match-details/match-details";
import TournamentView from "./screens/tournament/tournament";
import { setSettings } from "./features/settings/settings";
import { Dantam } from "./screens/dantam/dantam";
import TeamMatches from "./screens/team-matches/team-matches";
import SuperAdminPanel from "./screens/super-admin/super-admin";
import ContactPage from "./screens/other/contact";
import EditTeamList from "./screens/edit-team-list/EditTeamList";
import { setPlayers } from "./features/players/playersSlice";
import { setTournamentEvents, updateLocationOnAllTournamentEvents, updateTournamentEvent } from "./features/tournamentEvents/tournamentEventsSlice";
import ResetPassword from "./screens/other/resetPassword";
import PrivacyPolicy from "./screens/other/privacyPolicy";

let supabaseSubscription: RealtimeChannel;

function App() {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const toast = useAppSelector((state) => state.notification?.toast);
  const tournament = useAppSelector((state) => state.tournament.tournament);
  const settings = useAppSelector((state) => state.settings.settings);
  const isAdmin = useAppSelector((state) => state.app.admin);
  const locations = useAppSelector((state) => state.locations.locations);
  const groups = useAppSelector((state) => state.groups.groups);
  const teams = useAppSelector((state) => state.teams.teams);
  const puljer = useAppSelector((state) => state.puljer.puljer);
  const referees = useAppSelector((state) => state.referees.referees);
  const isApplicationBootstrapped = useAppSelector(
    (state) => state.app.isApplicationBootstrapped
  );

  useEffect(() => {
    if (toast) {
      setShowToast(true);
    }
  }, [toast]);

  const onToastCloseCallback = () => {
    setShowToast(false);
    dispatch(removeToast());
  };

  useEffect(() => {
    supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user.id) {
        fetchUserDetails(session.user.id).then((user) => {
          if (user) {
            dispatch(
              setAdmin({
                id: user.id,
                firstname: user.firstname ?? "",
                lastname: user.lastname ?? "",
                email: user.email ?? "",
                role: user.role ?? ROLES.EDITOR,
                number: user.number ?? "",
              })
            );
          } else {
            dispatch(setAdmin(undefined));
            signOut();
            NotificationService.showToast({
              text: `Could not fetch user data.`,
              severity: "error",
              duration: 3000,
            });
          }
        });
      } else {
        dispatch(setAdmin(undefined));
      }
    });
  }, []);

  useEffect(() => {
    bootstrap();

    return () => {
      supabaseSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    window.addEventListener("focus", reloadMatches);
    return () => window.removeEventListener("focus", reloadMatches);
  }, []);

  const reloadMatches = () => {
    isApplicationBootstrapped &&
      loadMatches(tournament, locations, puljer, groups, teams, referees);
  };

  const bootstrap = async () => {
    setLoading(true);

    const favoriteTeams = getFavoriteTeamsFromCookies();
    favoriteTeams && dispatch(setFavoriteTeams(favoriteTeams));

    const activeTournament = await getActiveTournament();

    if (!activeTournament!?.id) {
      setLoading(false);
      console.error("Fatal error. Could not fetch active tournament");
      return;
    }

    dispatch(setTournament(activeTournament));

    const { clubs, puljer, locations, tournament, teams, groups, settings, referees, matchReferees, tournamentEvents } =
      await getStaticTournamentData(activeTournament.id);
    dispatch(setClubs(clubs));
    dispatch(setPuljer(puljer));
    dispatch(setSelectedPulje(puljer[0]));
    dispatch(setLocations(locations));
    dispatch(setTournament(tournament));
    dispatch(setSettings(settings));
    dispatch(setReferees(referees));
    dispatch(setTournamentEvents(tournamentEvents));

    await loadMatches(activeTournament, locations, puljer, groups, teams, referees, matchReferees);
    dispatch(setIsApplicationBootstrapped(true));
    setLoading(false);
  };

  const loadMatches = async (
    tournament: Tournament,
    locations: Array<LocationType>,
    puljer: Array<Pulje>,
    groups: Array<GroupType>,
    teams: Array<TeamType>,
    referees: Array<Referee>,
    matchReferees?: Array<SpecielMatchReferee>,

  ) => {
    dispatch(setGroups(generateGroupsWithTeams(groups, teams)));
    dispatch(setTeams(generateTeamsWithGroup(teams, groups)));

    const matches = await getAllMatches(tournament.id);
    const events = await getAllEvents(tournament.id);
    const players = await getAllPlayers(tournament.id);

    dispatch(setPlayers(players.map(player => { return { ...player, team: teams.find(team => team.id === player.team) } })));

    const newMatches = matches.map((match: any) => {
      return {
        ...match,
        referees: matchReferees?.filter((referee: any) => referee.match === match.id),
        homeTeam: teams.find((team: TeamType) => team.id === match.homeTeam),
        awayTeam: teams.find((team: TeamType) => team.id === match.awayTeam),
        group: groups.find((group: GroupType) => group.id === match.group),
        location: locations.find((location) => location.id === match.location),
        pulje: puljer.find((pulje) => pulje.id === match.pulje),
        tournament: tournament,
        homeTeamEvents: events
          .filter(
            (event: any) =>
              event.match === match.id && event.team === match.homeTeam
          )
          .map((event: any) => {
            return {
              ...event,
              player: players.find((player) => player.id === event.player),
              match: matches.find(match => match.id === event.match),
              team: teams.find(team => team.id === event.team)
            };
          }),
        awayTeamEvents: events
          .filter(
            (event: any) =>
              event.match === match.id && event.team === match.awayTeam
          )
          .map((event: any) => {
            return {
              ...event,
              player: players.find((player) => player.id === event.player),
              match: matches.find(match => match.id === event.match),
              team: teams.find(team => team.id === event.team)
            };
          }),
      } as Match;
    });
    dispatch(setGroupMatches(newMatches));

    supabaseSubscription = supabase
      .channel("custom-all-channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "matches" },
        (payload: any) => {
          const updatedMatch = {
            ...payload.new,
            homeTeam: teams.find(
              (team: TeamType) => team.id === payload.new.homeTeam
            ),
            awayTeam: teams.find(
              (team: TeamType) => team.id === payload.new.awayTeam
            ),
            group: groups.find(
              (group: GroupType) => group.id === payload.new.group
            ),
            location: locations.find(
              (location) => location.id === payload.new.location
            ),
            pulje: puljer.find((pulje) => pulje.id === payload.new.pulje),
            tournament: tournament,
          } as Match;
          dispatch(updateGroupMatch(updatedMatch));
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "locations" },
        (payload: any) => {
          dispatch(updateLocation(payload.new));
          dispatch(
            updateLocationOnAllMatches({
              locationId: payload.new.id,
              delay: payload.new.delay,
            })
          );
          dispatch(
            updateLocationOnAllTournamentEvents({
              locationId: payload.new.id,
              delay: payload.new.delay,
            })
          );
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tournament_events" },
        (payload: any) => {
          dispatch(updateTournamentEvent({ ...payload.new, location: locations.find((location) => location.id === payload.new.location), }))
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "match_events" },
        (payload: any) => {
          if (payload.eventType === "INSERT") {
            dispatch(addEvent(payload.new));
          } else if (payload.eventType === "UPDATE") {
            dispatch(
              updateEvent({
                eventId: payload.new.id,
                player: players.find(
                  (player) => player.id === payload.new.player
                ),
                matchId: payload.new.match,
                eventNumber: payload.new.eventNumber
              })
            );
          } else if (payload.eventType === "DELETE") {
            dispatch(deleteEvent({ id: payload.old.id }));
          }
        }
      )
      .subscribe();
  };

  const mainContent = () => {
    return (
      <Routes>
        <Route path="/" element={<Matches />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/match-details/:id" element={<MatchDetails />} />
        <Route path="/team-matches" element={<TeamMatches />} />
        <Route path="/favorite-matches" element={<Matches />} />
        <Route path="/table" element={<Table />} />
        <Route path="/tournament" element={<TournamentView />} />
        <Route path="/knockout" element={<KnockOut />} />
        <Route path="*" element={<ErrorPage />} />
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/super-admin/*" element={<SuperAdminPanel />} />
        <Route path="/slideshow" element={<Slideshow />} />
        <Route path="/dantam" element={<Dantam />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/edit-team-list/:id" element={<EditTeamList />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  };

  return (
    <div className="App">
      {showToast && toast && (
        <Toast toast={toast} onClose={onToastCloseCallback} />
      )}
      <>
        {loading ? (
          <LoadingIcon />
        ) : (
          <>
            {(settings?.isPublic || isAdmin) && tournament?.isActive ? (
              mainContent()
            ) : (
              <Routes>
                <Route path="/" element={<FrontPage />} />
                <Route path="/admin/*" element={<Admin />} />
                <Route path="/super-admin/*" element={<SuperAdminPanel />} />
                <Route path="/edit-team-list/:id" element={<EditTeamList />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<ErrorPage />} />
              </Routes>
            )}
          </>
        )}
      </>
    </div>
  );
}

export const buttonStyle: SxProps = {
  borderRadius: "10px",
  height: "30px",
  fontSize: "12px",
  fontWeight: "700",
  padding: "10px",
  backgroundColor: "rgb(99, 102, 241)",
  color: "white",

  ":hover": {
    backgroundColor: "rgb(67, 56, 202)",
  },
};

export default App;
